import type {
  ComposerSnapshot,
  ComposerAttachment,
  ComposerContent,
  AttachmentConfig,
  AttachmentInput,
  AddFilesOptions,
} from "./types";

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
};

/**
 * Manages composer state for multimodal message composition.
 *
 * Handles text input, file attachments (upload, validation, retry),
 * preview URL lifecycle, and message sending via an injected callback.
 *
 * Implements the useSyncExternalStore protocol (subscribe + getSnapshot).
 */
export class ComposerManager<
  TMeta extends Record<string, unknown> = Record<string, unknown>,
> {
  #text = "";
  #attachments: ComposerAttachment<TMeta>[] = [];
  #pendingMetadata: Record<string, unknown> | null = null;
  #attachmentConfig: AttachmentConfig<TMeta> | undefined;
  #onSend: ((content: ComposerContent) => void) | undefined;
  #listeners = new Set<() => void>();

  // Snapshot caching for React reference stability
  #cachedSnapshot: ComposerSnapshot<TMeta> | null = null;
  #lastText = "";
  #lastAttachments: ComposerAttachment<TMeta>[] = [];

  constructor(
    onSend?: (content: ComposerContent) => void,
    attachmentConfig?: AttachmentConfig<TMeta>,
  ) {
    this.#onSend = onSend;
    this.#attachmentConfig = attachmentConfig;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Configuration (mutable — refs can change between renders)
  // ─────────────────────────────────────────────────────────────────────

  setOnSend(onSend: (content: ComposerContent) => void): void {
    this.#onSend = onSend;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Read-only state
  // ─────────────────────────────────────────────────────────────────────

  get text(): string {
    return this.#text;
  }

  get attachments(): readonly ComposerAttachment<TMeta>[] {
    return this.#attachments;
  }

  get isUploading(): boolean {
    return this.#attachments.some((a) => a.status === "uploading");
  }

  get hasErrors(): boolean {
    return this.#attachments.some((a) => a.status === "error");
  }

  /**
   * Emptiness and readiness speak for the PLAIN send only, so they
   * consider untagged attachments: tagged ones belong to a panel and ride
   * out exclusively through that panel's submit, which gates itself.
   */
  get isEmpty(): boolean {
    return this.#text.trim() === "" && this.#untagged.length === 0;
  }

  get isReady(): boolean {
    const untagged = this.#untagged;
    const hasContent = this.#text.trim() !== "" || untagged.length > 0;
    return (
      hasContent &&
      !untagged.some((a) => a.status === "uploading") &&
      !untagged.some((a) => a.status === "error")
    );
  }

  get #untagged(): ComposerAttachment<TMeta>[] {
    return this.#attachments.filter((a) => !a.tag);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Actions (arrow functions for stable identity)
  // ─────────────────────────────────────────────────────────────────────

  setText = (text: string): void => {
    this.#text = text;
    // Emptying the input abandons any pinned suggestion — the pending
    // metadata describes text that no longer exists.
    if (text.trim() === "") this.#pendingMetadata = null;
    this.#notify();
  };

  /**
   * Pin a message-level sidecar onto the NEXT send (e.g. a prefilled
   * chip's `recommendation_id`). Survives edits to the text; replaced by
   * the next call; abandoned when the input is emptied; consumed by send.
   */
  setPendingMetadata = (metadata: Record<string, unknown> | null): void => {
    this.#pendingMetadata = metadata;
    this.#notify();
  };

  get pendingMetadata(): Record<string, unknown> | null {
    return this.#pendingMetadata;
  }

  addFiles = (files: FileList | File[], options?: AddFilesOptions): void => {
    for (const file of Array.from(files)) {
      this.#addFile(file, options);
    }
  };

  addAttachment = (input: AttachmentInput | string): void => {
    const url = typeof input === "string" ? input : input.url;
    const providedMimeType =
      typeof input === "string" ? undefined : input.mimeType;

    let mimeType = providedMimeType;
    if (!mimeType) {
      const ext = url.split(".").pop()?.toLowerCase().split("?")[0];
      mimeType = (ext && MIME_BY_EXTENSION[ext]) || "application/octet-stream";
    }

    this.#attachments = [
      ...this.#attachments,
      {
        id: crypto.randomUUID(),
        status: "completed",
        url,
        mimeType,
      },
    ];
    this.#notify();
  };

  removeAttachment = (id: string): void => {
    const attachment = this.#attachments.find((a) => a.id === id);
    if (attachment?.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
    this.#attachments = this.#attachments.filter((a) => a.id !== id);
    this.#notify();
  };

  removeAttachments = (
    predicate: (attachment: ComposerAttachment<TMeta>) => boolean,
  ): void => {
    const keep: ComposerAttachment<TMeta>[] = [];
    let removedAny = false;
    for (const a of this.#attachments) {
      if (predicate(a)) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
        removedAny = true;
      } else {
        keep.push(a);
      }
    }
    if (!removedAny) return;
    this.#attachments = keep;
    this.#notify();
  };

  retryAttachment = (id: string): void => {
    const attachment = this.#attachments.find((a) => a.id === id);
    if (attachment?.status === "error" && attachment.file) {
      this.#upload(attachment);
    }
  };

  clear = (): void => {
    for (const a of this.#attachments) {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    }
    this.#text = "";
    this.#attachments = [];
    this.#pendingMetadata = null;
    this.#notify();
  };

  /**
   * Assemble text + completed UNTAGGED attachments into ComposerContent,
   * call the onSend callback, then consume exactly what was sent. Tagged
   * attachments are a panel's working state: they neither ride along nor
   * get cleared here — only their panel's submit sends and removes them.
   *
   * No-op if the composer is not ready (!isReady).
   */
  sendMessage = (): void => {
    if (!this.isReady || !this.#onSend) return;

    const sent = this.#untagged.filter(
      (a) => a.status === "completed" && a.url,
    );
    const content: ComposerContent = {
      text: this.#text.trim(),
      attachments: sent.map((a) => ({
        url: a.url!,
        mimeType: a.mimeType || "application/octet-stream",
        filename: a.filename,
        // Pass meta through unchanged so per-attachment sidecar fields
        // (e.g. upload_id) survive into the submit payload's
        // additional_kwargs.metadata.attachments.
        meta: a.meta,
      })),
      ...(this.#pendingMetadata && { metadata: this.#pendingMetadata }),
    };

    this.#onSend(content);

    this.#text = "";
    this.#pendingMetadata = null;
    this.removeAttachments((a) => !a.tag);
    this.#notify();
  };

  // ─────────────────────────────────────────────────────────────────────
  // useSyncExternalStore protocol
  // ─────────────────────────────────────────────────────────────────────

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  getSnapshot = (): ComposerSnapshot<TMeta> => {
    const text = this.#text;
    const attachments = this.#attachments;

    if (
      this.#cachedSnapshot &&
      this.#lastText === text &&
      this.#lastAttachments === attachments
    ) {
      return this.#cachedSnapshot;
    }

    this.#cachedSnapshot = {
      text,
      attachments,
      isUploading: this.isUploading,
      hasErrors: this.hasErrors,
      isEmpty: this.isEmpty,
      isReady: this.isReady,
      setText: this.setText,
      addFiles: this.addFiles,
      addAttachment: this.addAttachment,
      removeAttachment: this.removeAttachment,
      removeAttachments: this.removeAttachments,
      retryAttachment: this.retryAttachment,
      clear: this.clear,
      sendMessage: this.sendMessage,
    };

    this.#lastText = text;
    this.#lastAttachments = attachments;

    return this.#cachedSnapshot;
  };

  // ─────────────────────────────────────────────────────────────────────
  // Private
  // ─────────────────────────────────────────────────────────────────────

  #notify = (): void => {
    for (const cb of this.#listeners) cb();
  };

  #addFile = (file: File, options?: AddFilesOptions): void => {
    if (this.#attachmentConfig?.validate) {
      const error = this.#attachmentConfig.validate(file);
      if (error) {
        this.#attachments = [
          ...this.#attachments,
          {
            id: crypto.randomUUID(),
            file,
            mimeType: file.type,
            status: "error",
            errorMessage: error,
            tag: options?.tag,
            tagLabel: options?.tagLabel,
          },
        ];
        this.#notify();
        return;
      }
    }

    const previewUrl = URL.createObjectURL(file);
    const attachment: ComposerAttachment<TMeta> = {
      id: crypto.randomUUID(),
      file,
      mimeType: file.type,
      previewUrl,
      status: "uploading",
      tag: options?.tag,
      tagLabel: options?.tagLabel,
    };
    this.#attachments = [...this.#attachments, attachment];
    this.#notify();

    this.#upload(attachment);
  };

  #upload = async (attachment: ComposerAttachment<TMeta>): Promise<void> => {
    this.#updateAttachment(attachment.id, {
      status: "uploading",
      errorMessage: undefined,
    });

    if (!this.#attachmentConfig?.upload) {
      this.#updateAttachment(attachment.id, {
        status: "error",
        errorMessage: "No upload function configured",
      });
      return;
    }

    try {
      const result = await this.#attachmentConfig.upload(attachment.file!);
      const meta = result.meta as Record<string, unknown> | undefined;
      const filename = meta?.filename as string | undefined;
      this.#updateAttachment(attachment.id, {
        status: "completed",
        url: result.url,
        filename,
        meta: result.meta as TMeta | undefined,
      });
    } catch (error) {
      this.#updateAttachment(attachment.id, {
        status: "error",
        errorMessage:
          error instanceof Error ? error.message : "Upload failed",
      });
    }
  };

  #updateAttachment = (
    id: string,
    updates: Partial<ComposerAttachment<TMeta>>,
  ): void => {
    this.#attachments = this.#attachments.map((a) =>
      a.id === id ? { ...a, ...updates } : a,
    );
    this.#notify();
  };
}
